/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import ClassicEditorBase from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import UploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder';
import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/list';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices';
import LinkImage from '@ckeditor/ckeditor5-link/src/linkimage';

/**
 * Custom Plugins
 **/

import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import HtmlEmbed from '@ckeditor/ckeditor5-html-embed/src/htmlembed';
import ObvioLeaderboardLink from './obvio-leaderboard-link';
import ObvioVariableSelector from './obvio-variable-selector';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize';
import FontColor from './obvio-font/src/fontcolor';
import FontBackgroundColor from './obvio-font/src/fontbackgroundcolor';
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import { ELEMENT_NAME } from './obvio-variable-selector/Value';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily';
import ObvioCustomButton from './obvio-custom-button/obviocustombutton';
import CustomButtonToolBar from './obvio-custom-button/custombuttontoolbar';
import CustomButtonMergeCodeLinks from './obvio-custom-button/custombuttonlink/custombuttonmergecodelinks';
import CustomTheme from './custom-theme';

// import './custom.css';

export default class ClassicEditor extends ClassicEditorBase {}

// Plugins to include in the build.
ClassicEditor.builtinPlugins = [
	Essentials,
	UploadAdapter,
	Autoformat,
	FontSize,
	FontColor,
	FontBackgroundColor,
	Bold,
	Italic,
	Underline,
	Strikethrough,
	Superscript,
	BlockQuote,
	CKFinder,
	CloudServices,
	EasyImage,
	Heading,
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	ImageResize,
	Indent,
	IndentBlock,
	Link,
	List,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	Table,
	TableToolbar,
	TextTransformation,
	Alignment,
	HtmlEmbed,
	ObvioLeaderboardLink,
	ObvioVariableSelector,
	FontFamily,
	LinkImage,
	ObvioCustomButton,
	CustomButtonToolBar,
	CustomButtonMergeCodeLinks,
	CustomTheme
];

// Editor configuration.
ClassicEditor.defaultConfig = {
	toolbar: {
		items: [
			'heading',
			'|',
			'fontsize',
			'fontColor',
			'fontBackgroundColor',
			'alignment',
			'bold',
			'italic',
			'underline',
			'fontFamily',
			'strikethrough',
			'superscript',
			'link',
			'-',
			'bulletedList',
			'numberedList',
			'|',
			'outdent',
			'indent',
			'|',
			'uploadImage',
			'htmlEmbed',
			'blockQuote',
			'insertTable',
			'mediaEmbed',
			'undo',
			'redo',
			ELEMENT_NAME,
			'customButton',
			'customTheme'
		],
		shouldNotGroupWhenFull: true
	},
	image: {
		resizeUnit: 'px',
		toolbar: [
			'imageStyle:alignLeft',
			'imageStyle:alignCenter',
			'imageStyle:alignRight',
			'|',
			'imageTextAlternative',
			'linkImage'
		],
		styles: [
			// This represents an image aligned to the left.
			'alignLeft',

			// This represents an image aligned to the left.
			'alignCenter',

			// This represents an image aligned to the right.
			'alignRight'
		]
	},
	table: {
		contentToolbar: [ 'tableColumn', 'tableRow', 'mergeTableCells' ]
	},
	links: {
		// default should be the leaderboard link.
		rexlink: [ 'Leaderboard' ]
	},
	link: {
		// Enable opening links in new tab
		addTargetToExternalLinks: true
	},
	customButton: {
		mergeCode: '{{login_url}}'
	},
	obvioVariables: {
		// Selectable variables are added here
		items: []
	},
	fontSize: {
		options: [ 9, 10, 11, 12, 'default', 14, 15, 16, 17 ],
		supportAllValues: true
	},
	fontFamily: {
		options: [
			'default',
			'Arial',
			'Courier New',
			'Montserrat',
			'Open Sans',
			'Oswald',
			'Pacifico',
			'Permanent Marker',
			'Poppins',
			'Roboto',
			'Shadows Into Light',
			'Times New Roman',
			'Ubuntu',
			'Verdana',
			'Water Brush'
		]
	},
	theme: {
		mode: 'Auto',
		callback: () => {}
	},
	// This value must be kept in sync with the language defined in webpack.config.js.
	language: 'en'
};
